import { z } from 'zod';

import { createOffsetPaginationRequestQuerySchema } from './pagination';
import { PrimaryKeySchema, commaDelimitedList } from './utils';

// Define the possible values for dataset version status (shared by schema and data versions)
export const DatasetVersionStatusEnum = z.enum([
  'draft',
  'current',
  'archived',
]);

const SkipRowIndexSchema = z.number().int().nonnegative();

export const DatasetSchemaVersionParsingOptionsSchema = z.object({
  skipRows: z
    .union([z.array(SkipRowIndexSchema), SkipRowIndexSchema])
    .transform((val) => {
      if (Array.isArray(val)) {
        return val;
      }
      return [val];
    })
    .optional(),
  headerRow: z.number().int().positive().optional(),
  delimiter: z.string().optional(),
  encoding: z.string().optional(),
});

export type DatasetVersionStatus = z.infer<typeof DatasetVersionStatusEnum>;

// Backward compatibility alias
export type DatasetSchemaVersionStatus = DatasetVersionStatus;

// Define parameter types for operations
const OperationParameterValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  z.array(z.string()),
  z.record(z.string(), z.unknown()),
]);

// Define specific operation parameter schemas
const StripWhitespaceParametersSchema = z.object({
  chars: z.string().nullable().optional(),
  sides: z.enum(['both', 'left', 'right']).optional(),
});

const ConvertDtypeParametersSchema = z.object({
  target_type: z
    .enum(['string', 'integer', 'float', 'datetime', 'boolean'])
    .optional(),
  handle_floats: z.boolean().optional(),
  coerce_errors: z.boolean().optional(),
  date_format: z.string().nullable().optional(),
});

const ValidateRangeParametersSchema = z.object({
  min_value: z.number().nullable().optional(),
  max_value: z.number().nullable().optional(),
  inclusive: z.boolean().optional(),
});

const ExtractPatternParametersSchema = z.object({
  patterns: z.array(z.string()).optional(),
  case_sensitive: z.boolean().optional(),
  return_matches: z.boolean().optional(),
  clean_trailing: z.string().optional(),
  extract_type: z.enum(['matches', 'groups', 'named_groups']).optional(),
});

// Operation schema with type-safe parameters
export const OperationSchema = z.union([
  z.object({
    type: z.literal('strip_whitespace'),
    parameters: StripWhitespaceParametersSchema.optional(),
  }),
  z.object({
    type: z.literal('convert_dtype'),
    parameters: ConvertDtypeParametersSchema.optional(),
  }),
  z.object({
    type: z.literal('validate_range'),
    parameters: ValidateRangeParametersSchema.optional(),
  }),
  z.object({
    type: z.literal('extract_pattern'),
    parameters: ExtractPatternParametersSchema.optional(),
  }),
  // Fallback for unknown operation types
  z.object({
    type: z.string(),
    parameters: z.record(z.string(), OperationParameterValueSchema).optional(),
  }),
]);

export type Operation = z.infer<typeof OperationSchema>;

// Define the column schema
export const ColumnSchema = z.object({
  pos: z.number().int().positive().optional(),
  name: z.string().min(1),
  source: z.string().optional(),
  type: z.string(),
  nullable: z.boolean(),
  transformExpr: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  operations: z.array(OperationSchema).optional(),
});

export type Column = z.infer<typeof ColumnSchema>;

export const DatasetSchemaVersionSchema = z.object({
  datasetSchemaVersionId: PrimaryKeySchema,
  datasetId: PrimaryKeySchema,
  versionNo: z.number().int().positive(),
  columnsJson: z.array(ColumnSchema),
  status: DatasetVersionStatusEnum,
  parsingOptions: DatasetSchemaVersionParsingOptionsSchema.optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type DatasetSchemaVersion = z.infer<typeof DatasetSchemaVersionSchema>;

export const DatasetSchemaVersionsSchema = z.array(DatasetSchemaVersionSchema);

export type DatasetSchemaVersions = z.infer<typeof DatasetSchemaVersionsSchema>;

export const DatasetSchemaVersionsQuerySchema = z
  .object({
    datasetSchemaVersionIds: commaDelimitedList(
      PrimaryKeySchema.array(),
    ).optional(),
    datasetIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
    status: DatasetVersionStatusEnum.optional(),
    versionNo: z.coerce.number().int().positive().optional(),
  })
  .merge(
    createOffsetPaginationRequestQuerySchema(
      z.enum(['createdAt', 'updatedAt', 'versionNo']),
    ),
  );

export type DatasetSchemaVersionsQuery = z.infer<
  typeof DatasetSchemaVersionsQuerySchema
>;

export const CreateDatasetSchemaVersionRequestSchema = z.object({
  datasetId: PrimaryKeySchema,
  columnsJson: z.array(ColumnSchema),
  status: DatasetVersionStatusEnum.optional().default('draft'),
  parsingOptions: DatasetSchemaVersionParsingOptionsSchema.optional(),
});

export type CreateDatasetSchemaVersionRequest = z.infer<
  typeof CreateDatasetSchemaVersionRequestSchema
>;

export const UpdateDatasetSchemaVersionRequestSchema = z.object({
  status: DatasetVersionStatusEnum.optional(),
  columnsJson: z.array(ColumnSchema).optional(),
  parsingOptions: z
    .object({
      skipRows: z
        .union([z.array(SkipRowIndexSchema), SkipRowIndexSchema])
        .transform((val) => {
          if (Array.isArray(val)) {
            return val;
          }
          return [val];
        })
        .optional(),
      headerRow: z.number().int().positive().optional(),
      delimiter: z.string().optional(),
      encoding: z.string().optional(),
    })
    .optional(),
});

export type UpdateDatasetSchemaVersionRequest = z.infer<
  typeof UpdateDatasetSchemaVersionRequestSchema
>;
